import React, { FunctionComponent, ReactNode, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { isCallable } from '@afterpay/utils'

import styles from './style.module.css'

export interface StickyProps {
  mode?: 'top' | 'bottom'
  onChange?: (fixed: boolean) => void
  onVisibleChange?: (isVisible: boolean) => void
  container?: () => Element | Document
  children: ReactNode | ((isFixed: boolean) => JSX.Element)
  isFixedOverride?: boolean
}

export const Sticky: FunctionComponent<StickyProps> = ({
  mode = 'top',
  onChange = () => {},
  onVisibleChange = () => {},
  container = () => null,
  children,
  isFixedOverride = null,
}) => {
  const [isFixedValue, setFixed] = useState(isFixedOverride !== null ? isFixedOverride : false)
  const isFixed = isFixedOverride !== null ? isFixedOverride : isFixedValue
  const ref = useRef<HTMLDivElement | null>(null)

  const stickyClassName = clsx(styles[`${mode}`], {
    [styles.fixed]: isFixed,
  })

  const handleChange = (fixed: boolean): void => {
    if (isFixedOverride === null) {
      setFixed(fixed)
    }
    onChange(fixed)
  }

  const handleIsIntersecting = (entry): void => {
    onVisibleChange(entry.isIntersecting && entry.intersectionRatio >= 0.99)
  }

  useEffect(() => {
    const element = ref.current
    if (element === null) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0]
        handleIsIntersecting(target)
        handleChange(target.intersectionRatio < 1)
      },
      {
        threshold: [1],
        root: container(),
      }
    )
    observer.observe(element)

    return () => {
      observer.unobserve(element)
    }
  }, [])

  return (
    <div ref={ref} className={stickyClassName}>
      {isCallable(children) ? children(isFixed) : children}
    </div>
  )
}

Sticky.displayName = 'Sticky'
